import { CacheFunction } from "@pdfking/compatibility"
import type {
  Degree,
  Distance,
  Page,
  Percentage,
  Pixel,
  PlotMedia,
  PlotPath,
  PlotRect,
  PlotShape,
  PlotText,
  PlottingContext,
  Point,
  Rectangle,
  Scale,
  SpatialRectangle,
} from "@pdfking/renderer-api"
import {
  ComputeScale,
  CreateContext,
  HorizontalAlign,
  PlotFunction,
  Rotate,
  ToPdflibColor,
  VerticalAlign,
} from "@pdfking/renderer-api"
import { BlobToUint8, Identity, ToBlob } from "@pdfking/tfunc"
import type {
  PageBoundingBox,
  PDFEmbeddedPage,
  PDFFont,
  PDFPage,
} from "pdf-lib"
import type { CMYK, RGB } from "pdf-lib"
import {
  closePath,
  cmyk,
  degrees,
  lineTo,
  moveTo,
  PDFDocument,
  popGraphicsState,
  pushGraphicsState,
  setLineWidth,
  setStrokingColor,
  StandardFonts, // revert to 180a53d068 to restore custom font
  stroke,
} from "pdf-lib"

import { BytesToPdf } from "../utils/BytesToPdf"

/**
 * Type representing the context used in the current implementation
 * of the PlotPage function. This combines the intended PlottingContext
 * type with all additional properties actually used in the code.
 */

type ColorAlpha = {
  color: CMYK | RGB | undefined
  opacity: number
}

type PageBlob = {
  page: PDFPage
  scale: Scale
}

type CurrentPlottingContext = {
  // PDF-lib related objects
  doc: PDFDocument
  page: PDFPage
  font: PDFFont

  scale_bleed: Pixel
  stroke_width: Pixel
  font_height_adjustment: Percentage

  // Coordinate transformation methods
  get_xy: (point: Point<Distance>) => Point<Pixel>
  get_measures: (
    position: SpatialRectangle<Distance>
  ) => SpatialRectangle<Pixel>
  get_color: (id: string) => ColorAlpha

  // PDF page retrieval methods
  get_page: (blob_id: string) => Promise<PageBlob | undefined>
  get_embedded_page: (
    page: PDFPage,
    mask: PageBoundingBox
  ) => Promise<PDFEmbeddedPage>
}

const white = cmyk(0, 0, 0, 0) // undefined // can be white instead but it is better to avoid transparency

const todegree = (angle: Degree) => degrees(-(angle || 0))

const CreateCurrentPlottingContext = async (
  context: Required<PlottingContext>,
  drawing: Page
): Promise<CurrentPlottingContext> => {
  const to_px = (value: Distance): Pixel =>
    (context.unit_to_px * value) as Pixel

  const get_size = ({
    width,
    height,
  }: Rectangle<Distance>): Rectangle<Pixel> => ({
    width: to_px(width) as Pixel,
    height: to_px(height) as Pixel,
  })

  const get_xy = ({ x: px, y: py }: Point<Distance>): Point<Pixel> => ({
    x: (bleed + to_px(px)) as Pixel,
    y: (bleed - to_px(py) + height) as Pixel,
  })

  const get_measures = (
    r: SpatialRectangle<Distance>
  ): SpatialRectangle<Pixel> => {
    const xy = get_xy(r)
    const size = get_size(r)

    return {
      x: xy.x,
      y: (xy.y - size.height) as Pixel,
      ...size,
    }
  }

  const { width, height } = get_size(drawing)
  const bleed = to_px(drawing.bleed ?? context.page_bleed)
  const rotation = todegree(0 as Degree)

  const get_color = (id: string): ColorAlpha => {
    const color = context.color_map[id]
    return color
      ? { opacity: color.a, color: ToPdflibColor(color) }
      : { opacity: 0, color: undefined }
  }

  const get_page = async (blob_id: string): Promise<PageBlob | undefined> => {
    const promise = context.file_map[blob_id]
    if (promise) {
      const blob = await promise
      const doc = await BytesToPdf(
        await BlobToUint8(blob),
        blob.type,
        context.image_resolution
      )
      if (doc.getPageCount() == 1) {
        return { scale: context.scale_map[blob_id], page: doc.getPage(0) }
      }
      throw new Error("PDF must have exactly one page")
    }
    return undefined
  }

  const get_embedded_page = async (
    page: PDFPage,
    mask: PageBoundingBox
  ): Promise<PDFEmbeddedPage | undefined> => {
    // this is done becose an empty page raises an errors
    const { Contents } = page
      ? page.node.normalizedEntries()
      : { Contents: undefined }

    return Contents ? await doc.embedPage(page, mask) : undefined
  }

  const doc = await PDFDocument.create()

  const font = await doc.embedFont(StandardFonts.Helvetica)

  const page = doc.addPage([width + bleed * 2, height + bleed * 2])
  page.setBleedBox(0, 0, width + bleed * 2, height + bleed * 2)
  page.setTrimBox(bleed, bleed, width, height)
  page.setMediaBox(0, 0, width + bleed * 2, height + bleed * 2)
  page.setRotation(rotation)
  page.setFont(font)

  return {
    doc,
    font,
    page,
    get_xy,
    get_page: CacheFunction(get_page, Identity),
    get_measures,
    get_embedded_page,
    get_color,
    stroke_width: to_px(context.stroke_width),
    scale_bleed: to_px(context.scale_bleed),
    font_height_adjustment: context.font_height_adjustment,
  }
}

const plot_text = async (
  context: CurrentPlottingContext,
  info: PlotText
): Promise<void> => {
  if (info.stroke_color || info.fill_color) {
    plot_rect(context, {
      ...info,
      rotation: 0 as Degree,
      func: PlotFunction.PLOT_RECT,
    })
  }

  const fw = context.font.widthOfTextAtSize(info.text, info.font_size)
  const fh =
    context.font.heightAtSize(info.font_size) * context.font_height_adjustment

  const { x, y, width, height } = context.get_measures(info.position)

  const { x: w, y: h } = Rotate(
    0 as Pixel,
    0 as Pixel,
    width,
    height,
    info.rotation as Degree
  )

  let ew = 0
  let eh = 0

  if (info.horizontal_align == HorizontalAlign.LEFT) {
    ew = -(Math.abs(w) - fw) / 2
  } else if (info.horizontal_align == HorizontalAlign.RIGHT) {
    ew = +(Math.abs(w) - fw) / 2
  }
  if (info.vertical_align == VerticalAlign.TOP) {
    eh = +(Math.abs(h) - fh) / 2
  } else if (info.vertical_align == VerticalAlign.BOTTOM) {
    eh = -(Math.abs(h) - fh) / 2
  }

  const coord = Rotate(
    (x + width / 2) as Pixel,
    (y + height / 2) as Pixel,
    (x + width / 2 - fw / 2 + ew) as Pixel,
    (y + height / 2 - fh / 2 + eh) as Pixel,
    info.rotation
  )

  context.page.drawText(info.text, {
    ...coord,
    ...context.get_color(info.text_color),
    size: info.font_size,
    rotate: todegree(info.rotation),
    font: context.font,
  })
}

const plot_rect = async (
  context: CurrentPlottingContext,
  info: PlotRect
): Promise<void> => {
  const fill = context.get_color(info.fill_color)
  const stroke = context.get_color(info.stroke_color)

  const { x, y, width, height } = context.get_measures(info.position)

  const coord = Rotate(
    (x + width / 2) as Pixel,
    (y + height / 2) as Pixel,
    x,
    y,
    info.rotation
  )

  context.page.drawRectangle({
    ...coord,
    width,
    height,
    rotate: todegree(info.rotation),
    borderWidth: stroke ? context.stroke_width : 0,
    borderColor: stroke ? stroke.color : white,
    borderOpacity: stroke ? stroke.opacity : 0,
    color: fill ? fill.color : white,
    opacity: fill ? fill.opacity : 0,
  })
}

const plot_media = async (
  context: CurrentPlottingContext,
  info: PlotMedia
): Promise<void> => {
  const spec = await context.get_page(info.page)

  if (!spec) {
    return
  }

  context.page.drawRectangle({
    ...context.get_measures(info.crop_position),
    color: white,
  })

  const inner = spec.page.getTrimBox() as SpatialRectangle<Pixel>

  const media = context.get_measures(info.media_position)
  const crop = context.get_measures(info.crop_position)

  const { x: scale_x, y: scale_y } = ComputeScale<Pixel>(
    { outer: media, inner, bleed: context.scale_bleed },
    spec.scale || undefined
  )

  const { x: tx, y: ty, width: tw, height: th } = inner
  const { x: mx, y: my, width: mw, height: mh } = media
  const { x: cx, y: cy, width: cw, height: ch } = crop

  const center_x = (tx + tw / 2) as Pixel
  const center_y = (ty + th / 2) as Pixel

  const { x: left, y: bottom } = Rotate(
    center_x,
    center_y,
    (center_x - (mx + mw / 2 - cx) / scale_x) as Pixel,
    (center_y - (my + mh / 2 - cy) / scale_y) as Pixel,
    -info.rotation as Degree
  )
  const { x: right, y: top } = Rotate(
    center_x,
    center_y,
    (center_x + (mw / 2 + (cx + cw - (mx + mw))) / scale_x) as Pixel,
    (center_y + (mh / 2 + (cy + ch - (my + mh))) / scale_y) as Pixel,
    -info.rotation as Degree
  )

  const mask = { top, left, right, bottom }

  const embedded = await context.get_embedded_page(spec.page, mask)

  await context.doc.flush()

  if (embedded)
    context.page.drawPage(embedded, {
      rotate: todegree(info.rotation),
      x: cx,
      y: cy,

      //width: w,
      //height: h,
      //width: 100,
      //height: 100,
      xScale: scale_x,
      yScale: scale_y,
      //opacity: 0.75,
    })
}

const plot_path = async (
  context: CurrentPlottingContext,
  info: PlotPath
): Promise<void> => {
  const stroke_color = context.get_color(info.stroke_color)
  const [{ x, y }, ...points] = info.points.map(context.get_xy)

  context.page.pushOperators(
    pushGraphicsState(),
    setLineWidth(context.stroke_width),
    setStrokingColor(stroke_color.color),
    moveTo(x, y),
    ...points.map(({ x, y }) => lineTo(x, y)),
    ...(info.closed ? [closePath()] : []),
    stroke(),
    moveTo(0, 0),
    popGraphicsState()
  )
}

const handlers: Record<
  PlotFunction,
  (context: CurrentPlottingContext, shape: PlotShape) => Promise<void>
> = {
  [PlotFunction.PLOT_TEXT]: plot_text,
  [PlotFunction.PLOT_RECT]: plot_rect,
  [PlotFunction.PLOT_MEDIA]: plot_media,
  [PlotFunction.PLOT_PATH]: plot_path,
}

export const PlotPage = async (
  page: Page,
  partial: PlottingContext = {}
): Promise<Blob> => {
  const context = await CreateCurrentPlottingContext(
    CreateContext(partial),
    page
  )

  for (const shape of page.shapes) {
    await handlers[shape.func](context, shape)
  }

  return context.doc.save().then(bytes => ToBlob(bytes, "application/pdf"))
}
